import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { TextField, FormGroup, FormControl, FormLabel, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Enrollment } from './models/Enrollment';
import TopNavActions from './TopNavActions';
import ConfirmExitDialog from './ConfirmExitDialog';

interface ContactInformationViewProps {
  enrollment: Enrollment;
  onPageChange: (page: string) => void;
}

const phonePattern = /^[2-9][0-9]{2}[2-9][0-9]{2}[0-9]{4}$/;
const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,64}$/i;
const namePattern = /^[^<>&"\v$%!#?§;*~/\\|^=\[\]{}()\x00-\x1F]+$/;

const formatPhone = (value: string) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const ContactInformationView: React.FC<ContactInformationViewProps> = ({ enrollment, onPageChange }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });
  const [openExitDialog, setOpenExitDialog] = React.useState(false);
  const { t } = useTranslation('enrollment');

  useEffect(() => {
    setFirstName(enrollment.subjectInformation.firstName ?? '');
    setLastName(enrollment.subjectInformation.lastName ?? '');
    setEmail(enrollment.subjectInformation.emailAddress ?? '');
    setPhone(enrollment.subjectInformation.phoneNumber ?? '');
    // eslint-disable-next-line
  }, [enrollment]);

  const validate = () => ({
    firstName: firstName.trim() === '' ? t("First name is required.") : (!namePattern.test(firstName) ? t("First name contains invalid characters.") : ''),
    lastName: lastName.trim() === '' ? t("Last name is required.") : (!namePattern.test(lastName) ? t("Last name contains invalid characters.") : ''),
    email: !emailPattern.test(email.trim()) ? t("Enter a valid email address.") : '',
    phone: phone ? (!phonePattern.test(phone.replace(/\D/g, '')) ? t("Enter a valid 10-digit phone number.") : '') : '',
  });

  const errors = validate();
  const isValid = !Object.values(errors).some(Boolean);

  const handleNext = () => {
    enrollment.subjectInformation.firstName = firstName;
    enrollment.subjectInformation.lastName = lastName;
    enrollment.subjectInformation.emailAddress = email;
    enrollment.subjectInformation.phoneNumber = phone;
    onPageChange('review-and-enroll-view');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />

      <Typography variant="h4"  component='h1' id="page-heading" tabIndex={-1}>{t("Contact Information")}</Typography>
      <FormGroup>
        <FormControl fullWidth margin="normal">
          <FormLabel component="legend">
            <Typography variant="h6" component="div">{t("First Name")}</Typography>
          </FormLabel>
          <TextField
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            onBlur={() => setTouched(t => ({ ...t, firstName: true }))}
            fullWidth
            required={true}
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
            autoComplete='given-name'
            inputProps={{ "aria-label": t("First Name") }}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <FormLabel component="legend">
            <Typography variant="h6" component='div'>{t("Last Name")}</Typography>
          </FormLabel>
          <TextField
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            onBlur={() => setTouched(t => ({ ...t, lastName: true }))}
            fullWidth
            required={true}
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
            autoComplete='family-name'
            inputProps={{ "aria-label": t("Last Name") }}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <FormLabel component="legend">
            <Typography variant="h6" component='div'>{t("Phone Number")}</Typography>
          </FormLabel>
          <TextField
            value={phone}
            onChange={handlePhoneChange}
            onBlur={() => setTouched(t => ({ ...t, phone: true }))}
            onKeyPress={handlePhoneKeyPress}
            fullWidth
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
            inputProps={{ maxLength: 14, placeholder: '(555) 123-4567', "aria-label": t("Phone Number") }}
            autoComplete='tel'
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <FormLabel component="legend">
            <Typography variant="h6" component='div'>{t("Email")}</Typography>
          </FormLabel>
          <TextField
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={() => setTouched(t => ({ ...t, email: true }))}
            fullWidth
            required={true}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            autoComplete='email'
            inputProps={{ "aria-label": t("Email") }}
          />
        </FormControl>
      </FormGroup>
      <Button variant="contained" onClick={handleNext} disabled={!isValid} style={{ marginTop: 32, marginBottom: 32 }}>
        {t("Next")}
      </Button>
    </React.Fragment>
  );
};

export default ContactInformationView;
